import React, { useState, useRef } from 'react';
import { Document, type LinkedObject, type HighlightBubble } from '../Document';
import type { TagDefinition } from '../../molecules/TagPanel';
import type { TagColor } from '../../atoms/TagChip';
import { Paginator } from '../../molecules/Paginator';
import './DocumentFeed.css';

export interface DocumentData {
  /** Unique document ID */
  doc_id: string;
  /** Document type */
  documentType: string;
  /** Document date (ISO string) - the document's issue/creation date */
  documentDate: string;
  /** When the document was uploaded/received (ISO string) */
  uploadedAt?: string;
  /** Description */
  description?: string;
  /** Document status */
  status: 'completed' | 'processing' | 'error';
  /** Original filename for processing/error states */
  filename?: string;
  /** Smart filename for download (e.g., "Rechnung_Dünger_Jan_2023.pdf") */
  smartFilename?: string;
  /** Note/message */
  note?: string;
  /** Linked objects */
  linkedObjects?: LinkedObject[];
  /** Whether the document is waiting (queued, not actively processing) */
  isWaiting?: boolean;
  /** Taxonomy category (from INDEX.yaml) */
  category?: string;
  /** Taxonomy subcategory (from INDEX.yaml) */
  subcategory?: string;
  /** Extraction tier: 1=full, 2=recognition, 3=fallback */
  tier?: number;
  /** Icon name from feedDisplay.icon in document type YAML (lucide-react icon name) */
  icon?: string;
  /** Highlight bubbles for folded view (from highlighted_facts) */
  highlightBubbles?: HighlightBubble[];
  /** Whether this document has been soft-deleted */
  isDeleted?: boolean;
  /** Tag IDs assigned to this document */
  tagIds?: string[];
}

/** Sort mode for documents */
export type SortBy = 'document_date' | 'received_date';

export interface DocumentFeedProps {
  /** Array of documents to display */
  documents: DocumentData[];
  /** Handler for preview action (opens fullscreen document viewer) */
  onPreview?: (docId: string, filename?: string) => void;
  /** Handler for delete action */
  onDelete?: (docId: string) => void;
  /** Handler for restore action (for soft-deleted documents) */
  onRestore?: (docId: string) => void;
  /** Animation duration for expand/collapse */
  animationDuration?: number;
  /** Label for "This month" header (default: "Dieser Monat") */
  thisMonthLabel?: string;
  /** Label for the undated-documents group header (default: "Ohne Datum") — #1521 */
  undatedLabel?: string;
  /** Label for empty state (default: "No documents found") */
  noDocumentsFoundLabel?: string;
  /** Label for delete action in context menu (default: "Delete") */
  deleteLabel?: string;
  /** Label for preview action (default: "Preview") */
  previewLabel?: string;
  /** Label for restore action in context menu (default: "Restore") */
  restoreLabel?: string;
  /** Current sort mode - determines which date to display in headers */
  sortBy?: SortBy;
  // Pagination props
  /** Current page (0-indexed) */
  currentPage?: number;
  /** Items per page */
  pageSize?: number;
  /** Total number of items */
  totalItems?: number;
  /** Page change handler */
  onPageChange?: (page: number) => void;
  // Selection mode props
  /** Whether selection mode is active (shows checkboxes) */
  selectionMode?: boolean;
  /** Set of currently selected document IDs */
  selectedDocIds?: Set<string>;
  /** Handler when selection changes */
  onSelectionChange?: (docId: string, selected: boolean) => void;
  /** Whether viewing deleted documents (enables selection checkboxes) */
  showDeleted?: boolean;
  // Tag props (workspace-level, passed to each Document)
  /** All workspace tags */
  allTags?: TagDefinition[];
  /** Handler when a tag is toggled on/off for a document */
  onToggleTag?: (docId: string, tagId: string) => void;
  /** Handler to create a new workspace tag (docId for auto-apply on Enter) */
  onCreateTag?: (docId: string, name: string, color: TagColor) => void;
  /** Handler to edit a workspace tag */
  onEditTag?: (tagId: string, name: string, color: TagColor) => void;
  /** Handler to delete a workspace tag */
  onDeleteTag?: (tagId: string) => void;
  /** Label for tag trigger button */
  tagLabel?: string;
  /** Labels for tag panel */
  tagPanelLabels?: {
    placeholder?: string;
    done?: string;
    deleteTag?: string;
    cancel?: string;
  };
  /** Controlled expanded document ID (if provided, component is controlled) */
  expandedDocId?: string | null;
  /** Called when the expanded document changes */
  onExpandedChange?: (docId: string | null) => void;
  /** Returns the page count for ``docId``, or null/undefined to skip
   *  page-preview tiles (#755). Typically wired to React Query's
   *  ``useDocumentMetadata`` for the currently expanded document. */
  getPageCount?: (docId: string) => number | null | undefined;
  /** URL builder for a page thumbnail. Threaded through to the per-row
   *  ``<Document>`` so consumers don't need to know the URL shape. */
  pageThumbnailUrl?: (docId: string, page: number) => string;
  /** Click handler for a page-preview tile — fires with ``(docId, page)``. */
  onPreviewPage?: (docId: string, page: number) => void;
}

// #1521: the bucket for docs whose group date is missing/unparseable (e.g. v3 docs whose document_date
// #1499 hasn't yet populated). Sorts last and renders a neutral header — never a "Invalid Date" string.
const UNKNOWN_MONTH_KEY = 'unknown';

// Month Header Component (groups documents by month)
const MonthHeader: React.FC<{ monthKey: string; thisMonthLabel?: string; undatedLabel?: string }> = ({
  monthKey,
  thisMonthLabel = 'Dieser Monat',
  undatedLabel = 'Ohne Datum',
}) => {
  const formatMonth = (monthKeyStr: string) => {
    // #1521: the undated bucket gets a neutral label, not a parsed (and broken) date header.
    if (monthKeyStr === UNKNOWN_MONTH_KEY) {
      return undatedLabel;
    }
    // monthKey is in format YYYY-MM
    const [year, month] = monthKeyStr.split('-').map(Number);
    const inputDate = new Date(year, month - 1, 1);
    // Defensive: an unexpected non-YYYY-MM key would yield an Invalid Date — fall back to the neutral label.
    if (Number.isNaN(inputDate.getTime())) {
      return undatedLabel;
    }
    const today = new Date();

    // If the date is this month, show thisMonthLabel
    if (
      inputDate.getFullYear() === today.getFullYear() &&
      inputDate.getMonth() === today.getMonth()
    ) {
      return thisMonthLabel;
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
    };
    return inputDate.toLocaleDateString('de-DE', options);
  };

  return (
    <div className="document-feed-date-header">
      <span className="document-feed-date-text">{formatMonth(monthKey)}</span>
      <span className="document-feed-date-line" />
    </div>
  );
};

export const DocumentFeed: React.FC<DocumentFeedProps> = ({
  documents,
  onPreview,
  onDelete,
  onRestore,
  animationDuration = 0.3,
  thisMonthLabel = 'Dieser Monat',
  undatedLabel = 'Ohne Datum',
  noDocumentsFoundLabel = 'No documents found',
  deleteLabel = 'Delete',
  previewLabel = 'Preview',
  restoreLabel = 'Restore',
  sortBy = 'document_date',
  // Pagination
  currentPage = 0,
  pageSize = 20,
  totalItems = 0,
  onPageChange,
  // Selection mode
  selectionMode = false,
  selectedDocIds = new Set(),
  onSelectionChange,
  showDeleted = false,
  // Tags
  allTags,
  onToggleTag,
  onCreateTag,
  onEditTag,
  onDeleteTag,
  tagLabel,
  tagPanelLabels,
  expandedDocId: controlledExpandedDocId,
  onExpandedChange,
  getPageCount,
  pageThumbnailUrl,
  onPreviewPage,
}) => {
  const [internalExpandedDocId, setInternalExpandedDocId] = useState<string | null>(null);
  const isControlled = controlledExpandedDocId !== undefined;
  const expandedDocId = isControlled ? controlledExpandedDocId : internalExpandedDocId;

  // #1175 — deep-link scroll-to-target. When a doc is expanded (typically
  // because a ``/documents?doc=<id>`` deep-link seeded ``expandedDocId``),
  // scroll the matching feed row into view so the user sees it immediately
  // instead of landing at the top of the list.
  //
  // The scroll is driven from the expanded row's *ref callback* (see the
  // ``document-feed-row`` wrapper below), NOT a ``[expandedDocId]`` effect.
  // Why: on a direct ``/documents?doc=<id>`` open, the feed first renders
  // empty while ``useDocuments`` fetches, so an effect keyed on
  // ``expandedDocId`` runs once with the row absent (ref null) and never
  // re-fires after the rows arrive — the deep-linked row never scrolls
  // (the common initial-load case). The ref callback instead fires exactly
  // when the matching row's node mounts, whether that's synchronously
  // (already on the page) or after the async fetch resolves. The
  // ``scrolledForIdRef`` guard scrolls once per target id (inline refs
  // re-fire on every render). Windowing case is still a clean no-op: if the
  // row isn't on the current page its node never mounts, so nothing scrolls
  // (the page-level consumer navigates to the row's page/filter).
  const scrolledForIdRef = useRef<string | null>(null);

  const handleToggleDocument = (docId: string) => {
    const next = expandedDocId === docId ? null : docId;
    if (!isControlled) {
      setInternalExpandedDocId(next);
    }
    onExpandedChange?.(next);
  };

  // #1521: the YYYY-MM key for the current month — pinned first in groupDocumentsByMonth.
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Get the date to use for grouping based on sortBy mode
  const getGroupDate = (doc: DocumentData): string => {
    if (sortBy === 'received_date') {
      // When sorting by received date, use uploadedAt (falls back to documentDate)
      return doc.uploadedAt || doc.documentDate;
    }
    // Default: use document date
    return doc.documentDate;
  };

  // Group documents by month (YYYY-MM format)
  const groupDocumentsByMonth = (docs: DocumentData[]) => {
    const grouped: Record<string, DocumentData[]> = {};

    docs.forEach((doc) => {
      // Normalize to month string (YYYY-MM) to group documents from same month. #1521: undated docs (or
      // unparseable dates — #1499 leaves document_date "" for some v3 docs) land in the UNKNOWN_MONTH_KEY
      // bucket, which sorts LAST and renders a neutral header (never "Invalid Date").
      let monthKey = UNKNOWN_MONTH_KEY;
      const dateToUse = getGroupDate(doc);
      if (dateToUse) {
        const dateObj = new Date(dateToUse);
        // ``new Date(badStr)`` returns an Invalid Date (NaN time) rather than throwing, so guard explicitly.
        if (!Number.isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          monthKey = `${year}-${month}`;
        }
      }

      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(doc);
    });

    // #1521: pin the current month FIRST when it has docs, then the remaining dated months descending
    // (newest first), then the undated/Unknown bucket LAST. Plain ``localeCompare`` is wrong here:
    // "Unknown" > any "YYYY-MM" lexically, so it would otherwise float to the top above "Dieser Monat".
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === b) return 0;
        // Unknown bucket always last.
        if (a === UNKNOWN_MONTH_KEY) return 1;
        if (b === UNKNOWN_MONTH_KEY) return -1;
        // Current month always first.
        if (a === currentMonthKey) return -1;
        if (b === currentMonthKey) return 1;
        // Remaining dated months: newest first.
        return b.localeCompare(a);
      })
      .map((monthKey) => ({
        monthKey,
        documents: grouped[monthKey],
      }));
  };

  const documentGroups = groupDocumentsByMonth(documents);

  // Calculate global index for alternating row colors
  let globalIndex = 0;

  if (documents.length === 0) {
    return (
      <div className="document-feed-empty">
        <p>{noDocumentsFoundLabel}</p>
      </div>
    );
  }

  return (
    <div className="document-feed">
      {documentGroups.map((group) => (
        <div key={group.monthKey}>
          <MonthHeader monthKey={group.monthKey} thisMonthLabel={thisMonthLabel} undatedLabel={undatedLabel} />

          {group.documents.map((doc) => {
            const currentIndex = globalIndex++;
            // Compute enabled tags for this document
            const docTags = allTags && doc.tagIds
              ? allTags.filter((t) => doc.tagIds!.includes(t.id))
              : undefined;
            const isExpandedRow = expandedDocId === doc.doc_id;
            return (
              <div
                key={doc.doc_id}
                className="document-feed-row"
                data-doc-id={doc.doc_id}
                // #1175 — scroll the expanded row into view when its node
                // mounts. This fires on the async deep-link path too (the
                // row mounts only after the fetch resolves), which a
                // [expandedDocId] effect would miss. ``scrolledForIdRef``
                // guards against re-scrolling on every render (inline refs
                // re-fire each render) — we scroll once per target id.
                // ``scrollIntoView`` is a no-op in jsdom; the unit test stubs
                // it. ``block: 'center'`` keeps the panel off the sticky top.
                ref={(node) => {
                  if (isExpandedRow && node && scrolledForIdRef.current !== doc.doc_id) {
                    scrolledForIdRef.current = doc.doc_id;
                    node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                  }
                }}
              >
              <Document
                documentType={doc.documentType}
                documentDate={doc.documentDate}
                description={doc.description}
                linkedObjects={doc.linkedObjects}
                status={doc.status}
                filename={doc.filename}
                note={doc.note}
                isExpanded={expandedDocId === doc.doc_id}
                onToggle={() => handleToggleDocument(doc.doc_id)}
                isEvenRow={currentIndex % 2 === 0}
                animationDuration={animationDuration}
                onPreview={() => onPreview?.(doc.doc_id, doc.smartFilename || doc.filename)}
                onDelete={onDelete ? () => onDelete(doc.doc_id) : undefined}
                deleteLabel={deleteLabel}
                previewLabel={previewLabel}
                isWaiting={doc.isWaiting}
                category={doc.category}
                subcategory={doc.subcategory}
                tier={doc.tier}
                icon={doc.icon}
                highlightBubbles={doc.highlightBubbles}
                isSelectable={selectionMode || showDeleted}
                isSelected={selectedDocIds.has(doc.doc_id)}
                onSelectionChange={(selected) => onSelectionChange?.(doc.doc_id, selected)}
                isDeleted={doc.isDeleted}
                onRestore={onRestore ? () => onRestore(doc.doc_id) : undefined}
                restoreLabel={restoreLabel}
                tags={docTags}
                allTags={allTags}
                onToggleTag={onToggleTag ? (tagId: string) => onToggleTag(doc.doc_id, tagId) : undefined}
                onCreateTag={onCreateTag ? (name: string, color: TagColor) => onCreateTag(doc.doc_id, name, color) : undefined}
                onEditTag={onEditTag}
                onDeleteTag={onDeleteTag}
                tagLabel={tagLabel}
                tagPanelLabels={tagPanelLabels}
                pageCount={getPageCount ? getPageCount(doc.doc_id) : undefined}
                pageThumbnailUrl={
                  pageThumbnailUrl ? (page: number) => pageThumbnailUrl(doc.doc_id, page) : undefined
                }
                onPreviewPage={onPreviewPage ? (page: number) => onPreviewPage(doc.doc_id, page) : undefined}
              />
              </div>
            );
          })}
        </div>
      ))}

      {/* Paginator at the bottom */}
      {onPageChange && totalItems > 0 && (
        <Paginator
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default DocumentFeed;
